import { LIST_SEPARATOR } from './catalog';

/**
 * Reading one cell out of a row that has already been checked against its
 * section's header.
 *
 * Every value in the file is text, so "absent" is spelled as an empty cell and
 * has to be read back as `null` rather than as `0` or `''`. Keeping that rule in
 * one place is what stops one section from treating an empty number as zero
 * while another treats it as missing.
 */

/** The cell's text, or null when the cell is empty. */
export const cellText = (row: Readonly<Record<string, string>>, field: string): string | null => {
  const value = row[field];
  return value === undefined || value === '' ? null : value;
};

/** The cell's text, or an empty string — for fields the decoders read as text. */
export const cellString = (row: Readonly<Record<string, string>>, field: string): string =>
  row[field] ?? '';

/** The cell as a whole number, or null when it is empty or not one. */
export const cellInt = (row: Readonly<Record<string, string>>, field: string): number | null => {
  const value = cellText(row, field);
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
};

/** The cell as a finite number, or null when it is empty or not one. */
export const cellNumber = (row: Readonly<Record<string, string>>, field: string): number | null => {
  const value = cellText(row, field);
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The cell as a list of numbers, joined by `|`.
 *
 * Only ever used for values that are numeric by construction, so the separator
 * can never be ambiguous — a label with a `|` in it is never put through here.
 * An unreadable entry is dropped rather than failing the row, which matches how
 * the decoders treat a list they only half understand.
 */
export const cellNumbers = (
  row: Readonly<Record<string, string>>,
  field: string,
): readonly number[] => {
  const value = cellText(row, field);
  if (value === null) return [];
  return value
    .split(LIST_SEPARATOR)
    .flatMap((part) => {
      const parsed = Number(part.trim());
      return Number.isFinite(parsed) ? [parsed] : [];
    });
};

/** Writes a list of numbers back into one cell. */
export const listCell = (values: readonly number[]): string => values.join(LIST_SEPARATOR);

/** Writes an optional number as a cell: empty when it is absent. */
export const numberCell = (value: number | null): string => (value === null ? '' : String(value));

/** Writes optional text as a cell: empty when it is absent. */
export const textCell = (value: string | null): string => value ?? '';
